/*
  DocumentImports — Bulk-import review queue for GP referrals + letters.

  Layout: a "Scan Google Drive folder" toolbar on top, a list of import
  batches on the left, and the item-review pane on the right. The item
  pane shows the source document inline (PDF/image) plus the AI-extracted
  fields and candidate client matches; the reviewer can correct any of
  them and either Approve → attach-to-client or Reject.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import useStickyState from "@/hooks/useStickyState";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import ImportPauseBanner from "@/components/ImportPauseBanner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  FileText, FolderPlus, Loader2, CheckCircle2, XCircle, Search, User,
  ExternalLink, Cloud, AlertTriangle, ChevronRight, UserPlus, StopCircle,
  RotateCw, RefreshCw, X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import GDriveFolderPickerDialog from "@/components/GDriveFolderPickerDialog";

const CONFIDENCE_TONE = {
  high: "bg-emerald-50 text-emerald-800 border-emerald-300",
  medium: "bg-amber-50 text-amber-800 border-amber-300",
  low: "bg-orange-50 text-orange-800 border-orange-300",
  no_match: "bg-red-50 text-red-800 border-red-300",
};

const CATEGORY_OPTIONS = [
  "referral", "specialist_report", "gp_correspondence", "pathology",
  "imaging", "insurance", "workcover", "ndis", "consent", "other",
];


/**
 * BatchStatusChip — small colour-coded pill on every batch row so an
 * operator can spot processing / partial / review_ready at a glance
 * without inspecting the numeric counts underneath.
 *
 * Tones intentionally line up with the counts row below the chip so
 * operators build a colour memory (amber = something needs attention,
 * emerald = ready to walk through, indigo = in flight).
 */
const BATCH_STATUS_META = {
  processing:   { label: "processing",   tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  review_ready: { label: "review ready", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  partial:      { label: "partial",      tone: "bg-amber-50 text-amber-900 border-amber-200" },
  cancelled:    { label: "cancelled",    tone: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
function BatchStatusChip({ status }) {
  const meta = BATCH_STATUS_META[status] || {
    label: status || "unknown",
    tone: "bg-zinc-100 text-zinc-700 border-zinc-200",
  };
  return (
    <span
      className={`shrink-0 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.tone}`}
      data-testid={`batch-status-chip-${status || "unknown"}`}
      title={`Batch status: ${meta.label}`}
    >
      {meta.label}
    </span>
  );
}


export default function DocumentImports() {
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [activeItem, setActiveItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Items pagination — render 50 at a time so a batch of hundreds
  // of documents doesn't hammer React's reconciler. Backend already
  // supports `limit`/`offset` on /batches/{id}/items.
  const ITEMS_PAGE_SIZE = 50;
  const [itemsPage, setItemsPage] = useState(0);
  const [itemsTotal, setItemsTotal] = useState(0);
  const itemsPageRef = useRef(0);
  // Diagnostics snapshot for the active batch — small always-fast
  // payload (counts + last_error + latest 5 failed items) that the
  // UI polls independently of /items. Surfaces the failure inline so
  // the operator doesn't have to scan hundreds of rows to find a
  // problem.
  const [diagnostics, setDiagnostics] = useState(null);
  const [showAllFailures, setShowAllFailures] = useState(false);
  // Free-text filter over the items list — matches filename /
  // selected client email / extracted client name (case-insensitive).
  // Applied server-side so pagination stays consistent when the
  // batch is large.
  const [itemsSearch, setItemsSearch] = useState("");
  const itemsSearchRef = useRef("");
  // Batches-list filter (folder name) + ordering mode. Status
  // priority is the practical default: active work (processing,
  // partial, review_ready) rises to the top; cancelled batches
  // drift to the bottom without being hidden. Persisted to
  // localStorage so a morning refresh doesn't wipe the operator's
  // chosen view.
  const [batchesSearch, setBatchesSearch] = useStickyState("", "docImport.batchesSearch");
  const [batchesSort, setBatchesSort] = useStickyState("status", "docImport.batchesSort");  // "status" | "newest"

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/document-imports/batches");
      setBatches(r.data?.batches || []);
    } catch (e) {
      toast.error("Couldn't load batches", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadItems = useCallback(async (batchId, page) => {
    const targetPage = typeof page === "number" ? page : itemsPageRef.current;
    try {
      const params = {
        limit: ITEMS_PAGE_SIZE,
        offset: targetPage * ITEMS_PAGE_SIZE,
      };
      const needle = (itemsSearchRef.current || "").trim();
      if (needle) params.q = needle;
      const r = await api.get(`/document-imports/batches/${batchId}/items`, { params });
      const rows = r.data?.items || [];
      const total = r.data?.total ?? rows.length;
      // If the current page is now empty (last row was actioned) but
      // earlier pages still have rows, slide back one page so the
      // user isn't stuck on an empty view.
      if (rows.length === 0 && targetPage > 0 && total > 0) {
        const fallback = Math.max(0, Math.min(targetPage - 1, Math.ceil(total / ITEMS_PAGE_SIZE) - 1));
        return loadItems(batchId, fallback);
      }
      setItems(rows);
      setItemsTotal(total);
      setItemsPage(targetPage);
      itemsPageRef.current = targetPage;
    } catch (e) {
      toast.error("Couldn't load items", { description: e?.response?.data?.detail || e.message });
    }
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);
  // Refetch items only when the SELECTED BATCH changes — not every
  // time the progress counts tick. A 500-file batch that re-fetches
  // its full items list every few seconds during ingest can OOM the
  // origin pod or exceed the Cloudflare 100s timeout, producing the
  // "empty response / malformed headers" edge error. Selecting a
  // different batch (activeBatch.id changes) is the only case where
  // we actually need to reload.
  useEffect(() => {
    if (activeBatch?.id) {
      // Reset to page 0 whenever the operator switches batches — the
      // ref/state carries over from the previous batch otherwise.
      itemsPageRef.current = 0;
      setItemsPage(0);
      // Clear any in-flight filter so a different batch starts fresh.
      setItemsSearch("");
      itemsSearchRef.current = "";
      loadItems(activeBatch.id, 0);
    }
  }, [activeBatch?.id, loadItems]);

  // Debounce items search — 300ms after the last keystroke, refetch
  // page 0 of the active batch. Skips the very first render.
  const firstItemsSearchRun = useRef(true);
  useEffect(() => {
    itemsSearchRef.current = itemsSearch;
    if (firstItemsSearchRun.current) {
      firstItemsSearchRun.current = false;
      return;
    }
    if (!activeBatch?.id) return;
    const t = setTimeout(() => { loadItems(activeBatch.id, 0); }, 300);
    return () => clearTimeout(t);
  }, [itemsSearch, activeBatch?.id, loadItems]);

  // Lightweight diagnostics loader — a tiny always-fast payload the
  // UI can safely poll while /items would be huge or slow.
  const loadDiagnostics = useCallback(async (batchId) => {
    try {
      const r = await api.get(`/document-imports/batches/${batchId}/diagnostics`);
      setDiagnostics(r.data || null);
    } catch { /* silent — diagnostics is best-effort */ }
  }, []);

  // Fetch diagnostics whenever the selected batch changes, and re-poll
  // every 4s while it's still processing or has any failures — that's
  // when the operator most needs to see "which file blew up".
  useEffect(() => {
    if (!activeBatch?.id) { setDiagnostics(null); return; }
    setShowAllFailures(false);
    loadDiagnostics(activeBatch.id);
    const shouldPoll = activeBatch.status === "processing"
      || (activeBatch.counts?.failed || 0) > 0;
    if (!shouldPoll) return;
    const t = setInterval(() => loadDiagnostics(activeBatch.id), 4000);
    return () => clearInterval(t);
  }, [activeBatch?.id, activeBatch?.status, activeBatch?.counts?.failed,
      loadDiagnostics]);

  // Keep activeBatch in sync with the freshest batches array so the
  // "processing" banner and progress % update live while polling.
  useEffect(() => {
    if (!activeBatch) return;
    const fresh = batches.find((b) => b.id === activeBatch.id);
    if (fresh && (fresh.status !== activeBatch.status
                  || fresh.counts?.processed !== activeBatch.counts?.processed
                  || fresh.counts?.failed !== activeBatch.counts?.failed
                  || fresh.counts?.total !== activeBatch.counts?.total)) {
      setActiveBatch(fresh);
    }
  }, [batches]);

  async function scanFolder(folder) {
    setScanning(true);
    setPickerOpen(false);
    try {
      const r = await api.post("/document-imports/scan", {
        folder_id: folder.id, folder_name: folder.name,
      });
      const batchId = r.data.batch_id;
      const total = r.data.total || 0;
      toast.success(`Queued ${total} file(s) for background ingestion`, {
        description: "This runs in the background — you'll see items appear as they're processed.",
      });
      await loadBatches();

      // Auto-open the new batch + poll until every file has moved out
      // of the queued/processing state.
      const openBatch = (await api.get("/document-imports/batches")).data.batches
        .find((b) => b.id === batchId);
      if (openBatch) setActiveBatch(openBatch);
      pollBatch(batchId);
    } catch (e) {
      toast.error("Scan failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
    }
  }

  async function cancelBatch(batch) {
    if (!batch?.id) return;
    if (!window.confirm(
      `Cancel this import batch? All queued files (${batch.counts?.queued || 0}) will stop. ` +
      `Documents already processed and awaiting review are kept.`,
    )) return;
    try {
      const r = await api.post(`/document-imports/batches/${batch.id}/cancel`);
      toast.success(`Cancelled — stopped ${r.data.cancelled_items || 0} queued file(s)`);
      await loadBatches();
      if (activeBatch?.id === batch.id) await loadItems(batch.id);
    } catch (e) {
      toast.error("Cancel failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  // Nuclear fallback — the standard cancelBatch above sometimes hung
  // in production when the pod's event loop was blocked by a long
  // LLM/OCR call and the /cancel POST couldn't finish. force-cancel
  // hits the shortest possible DB path (no read, no counts recompute)
  // so it succeeds even when the box is under load. Kept behind a
  // typed-confirm dialog because it also cancels any file that was
  // mid-ingest at the moment of the click.
  async function nukeBatch(batch) {
    if (!batch?.id) return;
    const answer = window.prompt(
      `⚠ NUKE this batch?\n\n` +
      `This force-cancels every queued and in-flight file in "${batch.folder_name || batch.id}" ` +
      `WITHOUT waiting for the worker to notice. Pending-review documents are preserved.\n\n` +
      `Type NUKE to confirm.`,
    );
    if (answer !== "NUKE") return;
    try {
      const r = await api.post(
        `/document-imports/batches/${batch.id}/force-cancel`,
      );
      toast.success(
        `Batch nuked — force-cancelled ${r.data.cancelled_items || 0} file(s)`,
      );
      await loadBatches();
      if (activeBatch?.id === batch.id) await loadItems(batch.id);
    } catch (e) {
      toast.error("Nuke failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function resumeBatch(batch) {
    if (!batch?.id) return;
    try {
      const r = await api.post(`/document-imports/batches/${batch.id}/resume`);
      const resumed = r.data?.resumed_items || 0;
      if (resumed === 0) {
        toast.info("Nothing to resume — every file is already processed.");
      } else {
        toast.success(`Resumed ${resumed} file(s) — ingestion started again.`);
      }
      await loadBatches();
      if (activeBatch?.id === batch.id) {
        await loadItems(batch.id);
        // Kick the poller back on so the progress bar updates live.
        if (resumed > 0) pollBatch(batch.id);
      }
    } catch (e) {
      toast.error("Resume failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function retryFailedBatch(batch) {
    if (!batch?.id) return;
    try {
      const r = await api.post(`/document-imports/batches/${batch.id}/retry-failed`);
      const retried = r.data?.retried_items || 0;
      if (retried === 0) {
        toast.info("Nothing to retry — no failed files on this batch.");
      } else {
        toast.success(`Retrying ${retried} failed file(s)…`);
      }
      await loadBatches();
      if (activeBatch?.id === batch.id) {
        await loadItems(batch.id);
        if (retried > 0) pollBatch(batch.id);
      }
    } catch (e) {
      toast.error("Retry failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function rescanBatch(batch) {
    if (!batch?.id) return;
    try {
      const r = await api.post(`/document-imports/batches/${batch.id}/rescan`);
      const added = r.data?.added || 0;
      const scanned = r.data?.scanned || 0;
      if (added === 0) {
        toast.info(`Rescan complete — no new files on Drive (${scanned} scanned).`);
      } else {
        toast.success(
          `Found ${added} new file(s) — ingestion started.`,
          { description: `${scanned} scanned in the Drive folder.` },
        );
      }
      await loadBatches();
      if (activeBatch?.id === batch.id) {
        await loadItems(batch.id);
        if (added > 0) pollBatch(batch.id);
      }
    } catch (e) {
      toast.error("Rescan failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function pollBatch(batchId) {
    // Poll every 3s for up to 10 min. Refreshes both the batch header
    // (so counts update in the sidebar) and the item list so newly
    // ingested items become clickable without a manual refresh.
    const started = Date.now();
    const tick = async () => {
      try {
        const b = (await api.get(`/document-imports/batches/${batchId}`)).data;
        await loadBatches();
        setActiveBatch((prev) => (prev?.id === batchId ? { ...prev, ...b } : prev));
        if (activeBatch?.id === batchId || !activeBatch) {
          await loadItems(batchId);
        }
        const done = (b.status !== "processing") || Date.now() - started > 10 * 60_000;
        if (!done) setTimeout(tick, 3000);
        else if (b.status !== "processing") {
          toast.success(`Batch ready: ${b.counts?.processed || 0} document(s) to review`);
        }
      } catch { /* stop polling silently */ }
    };
    setTimeout(tick, 2500);
  }

  const refresh = async () => {
    await loadBatches();
    if (activeBatch) await loadItems(activeBatch.id);
    if (activeItem) {
      try {
        const r = await api.get(`/document-imports/items/${activeItem.id}`);
        setActiveItem(r.data);
      } catch { /* item may be gone */ }
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="document-imports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-700" />
            Document Import
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Bulk-scan a Google Drive folder of referrals, GP letters, and
            reports. Each document is auto-classified and matched to a
            client; you review + approve before it lands in the health record.
          </p>
        </div>
        <Button
          onClick={() => setPickerOpen(true)}
          disabled={scanning}
          data-testid="scan-drive-btn"
        >
          {scanning
            ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scanning…</>
            : <><Cloud className="h-4 w-4 mr-2" /> Scan Drive folder</>}
        </Button>
      </div>

      <ImportPauseBanner onChanged={loadBatches} />

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_1.4fr] gap-3 min-h-[70vh]">
        {/* Batches */}
        <Card className="p-3 rounded-lg border border-zinc-200 shadow-none overflow-y-auto">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500">Batches</div>
            {/*
              Sort mode — "status" surfaces active work first
              (processing → partial → review_ready → cancelled)
              with newest-first within each bucket. Cancelled
              batches still render so the operator can jump back
              to them; they just drift to the bottom.
            */}
            <select
              value={batchesSort}
              onChange={(e) => setBatchesSort(e.target.value)}
              className="h-6 text-2xs rounded border border-zinc-300 bg-white px-1 text-zinc-700"
              title="Sort batches"
              data-testid="batches-sort"
            >
              <option value="status">Status order</option>
              <option value="newest">Newest first</option>
            </select>
          </div>
          {/*
            Free-text filter — matches the Drive folder name so an
            operator with dozens of batches can jump straight to
            the right one without scrolling.
          */}
          <div className="relative mb-2">
            <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={batchesSearch}
              onChange={(e) => setBatchesSearch(e.target.value)}
              placeholder="Search folder name…"
              className="h-7 pl-7 pr-7 text-xs"
              data-testid="batches-search-input"
            />
            {batchesSearch && (
              <button
                type="button"
                onClick={() => setBatchesSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                aria-label="Clear search"
                data-testid="batches-search-clear"
              >
                <X className="h-3 w-3 text-zinc-500" />
              </button>
            )}
          </div>
          {loading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
            </div>
          ) : batches.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No batches yet.</div>
          ) : (() => {
            // Filter + sort in one memoised pass. Priority order
            // reflects "does the operator need to touch this soon?":
            // processing (in flight) > partial (some failures need
            // fixing) > review_ready (ready to walk through) >
            // cancelled (archived). Unknown statuses sink to the
            // bottom of the priority group so a schema change
            // doesn't hide new work.
            const STATUS_PRIORITY = {
              processing: 0, partial: 1, review_ready: 2, cancelled: 3,
            };
            const needle = batchesSearch.trim().toLowerCase();
            const filtered = needle
              ? batches.filter(
                  (b) => (b.folder_name || "").toLowerCase().includes(needle),
                )
              : batches;
            const sorted = batchesSort === "status"
              ? [...filtered].sort((a, b) => {
                  const pa = STATUS_PRIORITY[a.status] ?? 99;
                  const pb = STATUS_PRIORITY[b.status] ?? 99;
                  if (pa !== pb) return pa - pb;
                  // Newest within same status.
                  return String(b.created_at || "").localeCompare(String(a.created_at || ""));
                })
              : filtered;  // API already returns newest-first
            if (sorted.length === 0) {
              return (
                <div className="text-sm text-zinc-500 py-6 text-center"
                     data-testid="batches-empty">
                  No batches match “<b>{batchesSearch.trim()}</b>”.
                </div>
              );
            }
            return sorted.map((b) => {
              const total = b.counts?.total || 0;
              const processed = b.counts?.processed || 0;
              const failed = b.counts?.failed || 0;
              const done = processed + failed;
              const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
              const isProcessing = b.status === "processing";
              return (
                <button
                  key={b.id}
                  onClick={() => { setActiveBatch(b); setActiveItem(null); }}
                  className={cn(
                    "w-full text-left px-2 py-2 rounded hover:bg-zinc-100 mb-1 border",
                    activeBatch?.id === b.id ? "border-indigo-300 bg-indigo-50/40" : "border-transparent",
                  )}
                  data-testid={`batch-${b.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="text-sm font-medium truncate flex-1">
                      {b.folder_name || "Drive scan"}
                    </div>
                    <BatchStatusChip status={b.status} />
                  </div>
                  <div className="text-2xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>{processed}/{total}</span>
                    {isProcessing && (
                      <span className="text-indigo-700 inline-flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {b.counts?.queued || 0} processing
                      </span>
                    )}
                    {b.pending_count > 0 && (
                      <span className="text-amber-700">· {b.pending_count} pending</span>
                    )}
                    {failed > 0 && (
                      <span className="text-red-700">· {failed} failed</span>
                    )}
                  </div>
                  {isProcessing && total > 0 && (
                    <div className="mt-1.5" data-testid={`batch-progress-${b.id}`}>
                      <Progress
                        value={pct}
                        className="h-1.5 bg-indigo-100 [&>*]:bg-indigo-600"
                        data-testid={`batch-progress-bar-${b.id}`}
                      />
                      <div className="text-2xs text-indigo-700 mt-0.5 font-medium"
                           data-testid={`batch-progress-pct-${b.id}`}>
                        {pct}%
                      </div>
                    </div>
                  )}
                </button>
              );
            });
          })()}
        </Card>

        {/* Item list */}
        <Card className="p-3 rounded-lg border border-zinc-200 shadow-none overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500">
              {activeBatch ? `${itemsTotal} document(s)` : "Pick a batch"}
            </div>
            {activeBatch?.folder_id && (
              <Button
                size="sm" variant="outline"
                onClick={() => rescanBatch(activeBatch)}
                className="h-7 text-2xs"
                data-testid="rescan-batch-btn"
                title={`Rescan the "${activeBatch.folder_name || "Drive"}" folder for newly-added files`}
              >
                <RefreshCw className="h-3 w-3 mr-1" /> Rescan folder
              </Button>
            )}
          </div>
          {activeBatch && (
            <div className="relative mb-2">
              <Search className="h-3.5 w-3.5 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={itemsSearch}
                onChange={(e) => setItemsSearch(e.target.value)}
                placeholder="Search filename or client…"
                className="h-8 pl-7 pr-7 text-xs"
                data-testid="batch-items-search-input"
              />
              {itemsSearch && (
                <button
                  type="button"
                  onClick={() => setItemsSearch("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-zinc-100"
                  aria-label="Clear search"
                  data-testid="batch-items-search-clear"
                >
                  <X className="h-3 w-3 text-zinc-500" />
                </button>
              )}
            </div>
          )}
          {activeBatch && activeBatch.status === "processing" && (activeBatch.counts?.total || 0) > 0 && (
            <div className="mb-3 p-2 rounded border border-indigo-200 bg-indigo-50/60"
                 data-testid="active-batch-progress">
              <div className="flex items-center justify-between text-2xs text-indigo-800 mb-1">
                <span className="inline-flex items-center gap-1 font-medium">
                  <Loader2 className="h-3 w-3 animate-spin" /> Ingesting documents…
                </span>
                <span className="font-mono">
                  {((activeBatch.counts?.processed || 0) + (activeBatch.counts?.failed || 0))}/{activeBatch.counts?.total || 0}
                  {" · "}
                  {Math.min(100, Math.round((((activeBatch.counts?.processed || 0) + (activeBatch.counts?.failed || 0)) / (activeBatch.counts?.total || 1)) * 100))}%
                </span>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => cancelBatch(activeBatch)}
                  className="h-6 text-2xs text-red-700 hover:text-red-800 hover:bg-red-50"
                  data-testid="cancel-batch-btn"
                  title="Cancel this import — queued files stop; processed items are kept"
                >
                  <StopCircle className="h-3 w-3 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => nukeBatch(activeBatch)}
                  className="h-6 text-2xs text-red-900 hover:text-white hover:bg-red-700 border border-red-300"
                  data-testid="nuke-batch-btn"
                  title="Force-cancel — hits the shortest DB path. Use when Cancel appears stuck."
                >
                  <XCircle className="h-3 w-3 mr-1" /> Nuke
                </Button>
              </div>
              <Progress
                value={Math.min(100, Math.round((((activeBatch.counts?.processed || 0) + (activeBatch.counts?.failed || 0)) / (activeBatch.counts?.total || 1)) * 100))}
                className="h-2 bg-indigo-100 [&>*]:bg-indigo-600"
                data-testid="active-batch-progress-bar"
              />
            </div>
          )}
          {activeBatch && activeBatch.status === "cancelled" && (
            (() => {
              // A cancelled batch is "recoverable" when there are still
              // items that never finished — cancelled OR stranded in
              // queued/processing from an older worker. `_recompute_counts`
              // folds ``processing`` into ``queued`` so we only need to
              // read those two.
              const cancelled = activeBatch.counts?.cancelled || 0;
              const queued = activeBatch.counts?.queued || 0;
              const unfinished = cancelled + queued;
              const pending = activeBatch.counts?.pending_review || 0;
              if (unfinished === 0 && pending === 0) return null;
              return (
                <div className="mb-3 p-2 rounded border border-red-200 bg-red-50/60 flex items-center gap-2"
                     data-testid="batch-cancelled-banner">
                  <StopCircle className="h-3.5 w-3.5 text-red-700 flex-shrink-0" />
                  <span className="text-2xs text-red-800 flex-1">
                    Cancelled — {cancelled} file(s) never processed.
                    {queued > 0 && (
                      <> {queued} more file(s) were left stranded and can be resumed too.</>
                    )}
                    {pending > 0 && (
                      <> {pending} document(s) are still ready to review below.</>
                    )}
                  </span>
                  {unfinished > 0 && (
                    <Button
                      size="sm"
                      onClick={() => resumeBatch(activeBatch)}
                      className="h-7 text-2xs bg-indigo-600 hover:bg-indigo-700"
                      data-testid="resume-batch-btn"
                      title="Pick up where you left off — cancelled + stranded files re-queue"
                    >
                      <RotateCw className="h-3 w-3 mr-1" />
                      Resume the ones I cancelled ({unfinished})
                    </Button>
                  )}
                </div>
              );
            })()
          )}
          {activeBatch && (activeBatch.counts?.failed || 0) > 0 && (
            <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50/60 flex items-center gap-2"
                 data-testid="batch-failed-banner">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-700 flex-shrink-0" />
              <span className="text-2xs text-amber-800 flex-1">
                {activeBatch.counts.failed} file(s) failed ingestion — often a
                transient Drive / OCR hiccup. Retrying usually clears it.
                {activeBatch.status === "processing" && (
                  <span className="ml-1 text-amber-700">
                    (Safe to hit while the batch is still running — retried
                    items just re-queue.)
                  </span>
                )}
              </span>
              <Button
                size="sm"
                onClick={() => retryFailedBatch(activeBatch)}
                className="h-7 text-2xs bg-amber-600 hover:bg-amber-700"
                data-testid="retry-failed-batch-btn"
                title="Re-queue every failed file in this batch"
              >
                <RotateCw className="h-3 w-3 mr-1" />
                Retry only the failed files ({activeBatch.counts.failed})
              </Button>
            </div>
          )}
          {/* Diagnostics banner — surfaces the ACTUAL last error inline
              so the operator knows what blew up (storage timeout, OCR
              failure, LLM classifier error, etc.) without paging
              through hundreds of items. */}
          {activeBatch && diagnostics
            && (diagnostics.last_error
                || (diagnostics.recent_failures || []).length > 0) && (
            <div className="mb-3 p-2.5 rounded border border-red-300 bg-red-50/60"
                 data-testid="batch-diagnostics-banner">
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-red-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  {diagnostics.last_error && (
                    <div className="text-2xs text-red-800 mb-1">
                      <span className="font-semibold">Last failure: </span>
                      <span className="font-mono truncate inline-block max-w-[28ch] align-bottom"
                            title={diagnostics.last_error.filename || ""}>
                        {diagnostics.last_error.filename || "(unknown file)"}
                      </span>
                      <span className="text-red-700"> — </span>
                      <span className="text-red-900"
                            title={diagnostics.last_error.reason || ""}>
                        {(diagnostics.last_error.reason || "").slice(0, 140)}
                        {(diagnostics.last_error.reason || "").length > 140 ? "…" : ""}
                      </span>
                    </div>
                  )}
                  {(diagnostics.recent_failures || []).length > 0 && (
                    <>
                      <button
                        type="button"
                        className="text-2xs text-red-700 hover:text-red-900 underline"
                        onClick={() => setShowAllFailures((v) => !v)}
                        data-testid="batch-diagnostics-toggle"
                      >
                        {showAllFailures ? "Hide" : "Show"} {diagnostics.recent_failures.length} recent failure{diagnostics.recent_failures.length === 1 ? "" : "s"}
                      </button>
                      {showAllFailures && (
                        <ul className="mt-1.5 space-y-0.5 text-2xs text-red-900"
                            data-testid="batch-diagnostics-list">
                          {diagnostics.recent_failures.map((f, idx) => (
                            <li key={`${f.drive_file_id}-${idx}`} className="flex gap-1.5">
                              <span className="font-mono truncate max-w-[22ch] flex-shrink-0"
                                    title={f.filename || ""}>
                                {f.filename || "(unknown)"}
                              </span>
                              <span className="text-red-700">—</span>
                              <span className="truncate" title={f.failure_reason || ""}>
                                {(f.failure_reason || "").slice(0, 120)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </div>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => loadDiagnostics(activeBatch.id)}
                  className="h-6 text-2xs text-red-700 hover:text-red-800 hover:bg-red-100"
                  data-testid="batch-diagnostics-refresh"
                  title="Refresh diagnostics"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          {!activeBatch ? (
            <div className="text-sm text-zinc-500 py-6 text-center">
              Select a batch from the left.
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center" data-testid="items-empty">
              {itemsSearch.trim()
                ? <>No matches for “<b>{itemsSearch.trim()}</b>”.</>
                : "No items in this batch."}
            </div>
          ) : (
            items.map((it) => (
              <button
                key={it.id}
                onClick={() => setActiveItem(it)}
                className={cn(
                  "w-full text-left px-2 py-2 rounded hover:bg-zinc-100 mb-1 border",
                  activeItem?.id === it.id ? "border-indigo-300 bg-indigo-50/40" : "border-transparent",
                )}
                data-testid={`item-${it.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium truncate flex-1">{it.filename || "Untitled"}</div>
                  <StatusBadge status={it.status} />
                </div>
                <div className="text-2xs text-zinc-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <Badge className="text-2xs uppercase" variant="outline">
                    {(it.category || "other").replace(/_/g, " ")}
                  </Badge>
                  {it.category_confidence && (
                    <span className={cn("text-2xs uppercase px-1 rounded border",
                                          CONFIDENCE_TONE[it.category_confidence])}>
                      {it.category_confidence}
                    </span>
                  )}
                  {it.selected_client_email && (
                    <span className="text-emerald-700 truncate">
                      → {it.selected_client_email}
                    </span>
                  )}
                  {it.duplicate_of && (
                    <span className="text-orange-700"><AlertTriangle className="h-3 w-3 inline" /> duplicate</span>
                  )}
                </div>
              </button>
            ))
          )}
          {/* Pagination footer — surfaces only when the batch exceeds
              one page. We render 50 items at a time to keep large
              batches (hundreds of files) snappy in React. */}
          {activeBatch && itemsTotal > ITEMS_PAGE_SIZE && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-200 text-2xs text-zinc-600"
                 data-testid="items-pagination">
              <div>
                {items.length === 0
                  ? "0 of " + itemsTotal
                  : `${itemsPage * ITEMS_PAGE_SIZE + 1}–${itemsPage * ITEMS_PAGE_SIZE + items.length} of ${itemsTotal}`}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline"
                        className="h-6 px-2 text-2xs"
                        onClick={() => loadItems(activeBatch.id, itemsPage - 1)}
                        disabled={itemsPage <= 0}
                        data-testid="items-prev">
                  Prev
                </Button>
                <Button size="sm" variant="outline"
                        className="h-6 px-2 text-2xs"
                        onClick={() => loadItems(activeBatch.id, itemsPage + 1)}
                        disabled={(itemsPage + 1) * ITEMS_PAGE_SIZE >= itemsTotal}
                        data-testid="items-next">
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Reviewer pane */}
        <Card className="p-3 rounded-lg border border-zinc-200 shadow-none overflow-y-auto">
          {activeItem
            ? <ItemReview item={activeItem} onChanged={refresh} setActiveItem={setActiveItem} />
            : <div className="text-sm text-zinc-500 py-16 text-center">
                Pick an item to review.
              </div>}
        </Card>
      </div>

      <GDriveFolderPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={scanFolder}
      />
    </div>
  );
}


function StatusBadge({ status }) {
  const tone = {
    queued: "bg-indigo-50 text-indigo-700 border-indigo-300",
    processing: "bg-indigo-50 text-indigo-700 border-indigo-300",
    pending_review: "bg-amber-50 text-amber-800 border-amber-300",
    approved: "bg-emerald-50 text-emerald-800 border-emerald-300",
    rejected: "bg-zinc-100 text-zinc-500 border-zinc-300",
    failed: "bg-red-50 text-red-800 border-red-300",
  }[status] || "bg-zinc-100 text-zinc-700 border-zinc-300";
  return (
    <Badge className={cn("text-2xs uppercase border", tone)}>
      {(status || "").replace(/_/g, " ")}
    </Badge>
  );
}


function ItemReview({ item, onChanged, setActiveItem }) {
  const [fields, setFields] = useState(item.extracted || {});
  const [category, setCategory] = useState(item.category || "other");
  const [selectedEmail, setSelectedEmail] = useState(item.selected_client_email || "");
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [createClientOpen, setCreateClientOpen] = useState(false);

  useEffect(() => {
    setFields(item.extracted || {});
    setCategory(item.category || "other");
    setSelectedEmail(item.selected_client_email || "");
    setSearchResults([]);
  }, [item.id]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.patch(`/document-imports/items/${item.id}`, {
        category,
        selected_client_email: selectedEmail || null,
        extracted: fields,
      });
      setActiveItem(r.data);
      toast.success("Saved");
      await onChanged();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
      throw e;  // propagate so approve() can abort
    } finally { setSaving(false); }
  }

  async function approve() {
    // Button is disabled without a client, but re-check defensively.
    if (!selectedEmail) return;
    setApproving(true);
    try {
      await save();
      const r = await api.post(`/document-imports/items/${item.id}/approve`);
      toast.success("Attached to client record", {
        description: `Attachment ${r.data.attachment_id.slice(0, 8)}…`,
      });
      await onChanged();
      setActiveItem(null);
    } catch (e) {
      // Only show the approve-specific toast if we got past save().
      if (!e?.__savedRethrow) {
        toast.error("Approve failed", { description: e?.response?.data?.detail || e.message });
      }
    } finally { setApproving(false); }
  }

  async function reject() {
    setRejecting(true);
    try {
      await api.post(`/document-imports/items/${item.id}/reject`, {
        reason: (rejectReason || "").trim(),
      });
      toast.success("Rejected");
      setRejectOpen(false);
      setRejectReason("");
      await onChanged();
      setActiveItem(null);
    } catch (e) {
      toast.error("Reject failed", { description: e?.response?.data?.detail || e.message });
    } finally { setRejecting(false); }
  }

  async function runSearch() {
    if (search.trim().length < 2) { setSearchResults([]); return; }
    try {
      const r = await api.post(`/document-imports/items/${item.id}/search-clients`,
                                 { q: search.trim() });
      setSearchResults(r.data?.results || []);
    } catch (e) {
      toast.error("Search failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const isImage = (item.content_type || "").startsWith("image/");
  const fileUrl = `${API}/document-imports/items/${item.id}/file`;
  const locked = item.status !== "pending_review";

  // Suggested match banner — the top-scored candidate at any
  // confidence (per user spec: high/medium/low all get a suggestion).
  // The reviewer taps "Accept suggestion" to pre-fill the client
  // picker, then still needs to hit Approve to attach.
  const topSuggestion = (item.candidate_matches || [])[0] || null;
  const suggestionAlreadyPicked = topSuggestion
    && selectedEmail === topSuggestion.client_email;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-medium truncate flex-1">{item.filename}</h3>
        <StatusBadge status={item.status} />
      </div>

      {/* Suggested match banner — shown when the matcher found any
          candidate. Manual picker stays visible below, so the
          reviewer can ignore this banner if the suggestion is wrong.
      */}
      {!locked && topSuggestion && (
        <div className="flex items-center gap-3 rounded-md border border-emerald-300
                         bg-emerald-50 px-3 py-2"
             data-testid="suggested-match-banner">
          <div className="flex-1 min-w-0">
            <div className="text-2xs uppercase tracking-wide text-emerald-800
                             font-semibold flex items-center gap-1">
              <User className="h-3 w-3" /> Suggested match
              <span className={cn("ml-1 text-2xs px-1 rounded border",
                                    CONFIDENCE_TONE[topSuggestion.confidence])}>
                {topSuggestion.confidence}
              </span>
            </div>
            <div className="text-sm text-emerald-900 truncate">
              <b>{topSuggestion.client_name || topSuggestion.client_email}</b>
              <span className="text-emerald-700"> · {topSuggestion.client_email}</span>
            </div>
          </div>
          <Button
            size="sm"
            disabled={suggestionAlreadyPicked}
            onClick={() => setSelectedEmail(topSuggestion.client_email)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            data-testid="accept-suggested-match"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            {suggestionAlreadyPicked ? "Selected" : "Accept & attach"}
          </Button>
        </div>
      )}

      {/* File preview */}
      <div className="border border-zinc-200 rounded overflow-hidden bg-zinc-50" style={{ height: 340 }}>
        {isImage ? (
          <img src={fileUrl} alt={item.filename}
                className="w-full h-full object-contain" />
        ) : (
          <iframe title="source" src={fileUrl} className="w-full h-full border-0" />
        )}
      </div>

      {/* Category + client */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-2xs text-zinc-500 uppercase">Category</label>
          <select
            disabled={locked}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 border border-zinc-200 rounded px-2 text-sm bg-white"
            data-testid="review-category"
          >
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="text-2xs text-zinc-500 uppercase">Confidence</label>
          <div className={cn("h-9 px-2 border rounded text-sm inline-flex items-center",
                              CONFIDENCE_TONE[item.category_confidence] || "border-zinc-200")}>
            {item.category_confidence || "—"} · {item.category_method || "—"}
          </div>
        </div>
      </div>

      {/* Extracted fields */}
      <div className="grid grid-cols-2 gap-2">
        {[
          ["document_date", "Document date"],
          ["sender_name", "Sender"],
          ["sender_practice", "Practice"],
          ["diagnosis", "Diagnosis"],
          ["client_first_name", "Client first name"],
          ["client_last_name", "Client last name"],
          ["client_dob", "Client DOB"],
          ["client_phone", "Client phone"],
        ].map(([k, lbl]) => (
          <div key={k}>
            <label className="text-2xs text-zinc-500 uppercase">{lbl}</label>
            <Input
              disabled={locked}
              value={fields[k] || ""}
              onChange={(e) => setFields({ ...fields, [k]: e.target.value })}
              className="h-8 text-sm"
              data-testid={`review-field-${k}`}
            />
          </div>
        ))}
      </div>
      <div>
        <label className="text-2xs text-zinc-500 uppercase">Subject / referral reason</label>
        <Textarea
          disabled={locked}
          rows={2}
          value={fields.subject || ""}
          onChange={(e) => setFields({ ...fields, subject: e.target.value })}
          className="text-sm"
          data-testid="review-field-subject"
        />
      </div>

      {/* Client selection */}
      <div className="border border-zinc-200 rounded p-2 space-y-2">
        <div className="text-xs uppercase tracking-wide text-zinc-500 flex items-center gap-1">
          <User className="h-3 w-3" /> Client
        </div>
        <div className="space-y-1">
          {(item.candidate_matches || []).map((c, ci) => (
            <button
              key={ci}
              disabled={locked}
              onClick={() => setSelectedEmail(c.client_email)}
              className={cn(
                "w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded border",
                selectedEmail === c.client_email
                  ? "border-indigo-500 bg-indigo-50/40"
                  : "border-zinc-200 hover:bg-zinc-50",
              )}
              data-testid={`candidate-${ci}`}
            >
              <span className="flex-1 text-left truncate">
                <b>{c.client_name || c.client_email}</b>
                <span className="text-zinc-500"> · {c.client_email}</span>
              </span>
              <span className={cn("text-2xs uppercase px-1 rounded border",
                                    CONFIDENCE_TONE[c.confidence])}>
                {c.confidence}
              </span>
              <span className="text-2xs text-zinc-400">{c.score}</span>
            </button>
          ))}
        </div>
        {!locked && (
          <div className="flex items-center gap-2 pt-1">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search client directory…"
              className="h-8 text-sm flex-1"
              data-testid="client-search-input"
            />
            <Button size="sm" variant="outline" onClick={runSearch}>
              <Search className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="outline"
                    onClick={() => setCreateClientOpen(true)}
                    data-testid="create-client-btn"
                    title="Create a new client stub from the extracted fields">
              <UserPlus className="h-3.5 w-3.5 mr-1" /> New
            </Button>
          </div>
        )}
        {searchResults.length > 0 && (
          <div className="space-y-1 pt-1 border-t border-zinc-100">
            {searchResults.map((c, ci) => (
              <button
                key={ci}
                onClick={() => { setSelectedEmail(c.client_email); setSearchResults([]); }}
                className="w-full flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-zinc-100"
              >
                <ChevronRight className="h-3 w-3 text-zinc-400" />
                <span className="truncate flex-1 text-left">
                  <b>{c.client_name || c.client_email}</b>
                  <span className="text-zinc-500"> · {c.client_email}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div className="text-2xs text-zinc-500 pt-1">
          Selected: <span className="font-mono">{selectedEmail || "— none —"}</span>
        </div>
      </div>

      {/* Actions */}
      {!locked && (
        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" onClick={save} disabled={saving} data-testid="review-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
            Save
          </Button>
          <Button onClick={approve} disabled={approving || !selectedEmail} data-testid="review-approve">
            {approving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Approve → attach
          </Button>
          <Button variant="ghost" onClick={() => setRejectOpen(true)} disabled={rejecting} data-testid="review-reject">
            <XCircle className="h-3.5 w-3.5 mr-1 text-red-600" />
            Reject
          </Button>
          <div className="flex-1" />
          <a href={fileUrl} target="_blank" rel="noreferrer"
             className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1">
            Open original <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject this document</DialogTitle>
            <DialogDescription>
              The document stays in the batch history but is not attached to
              any client record. A reason is optional but helps future audits.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            placeholder="Optional reason — e.g. duplicate of another batch, wrong clinic"
            data-testid="reject-reason"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)} disabled={rejecting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={reject} disabled={rejecting} data-testid="reject-confirm">
              {rejecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateClientDialog
        open={createClientOpen}
        onOpenChange={setCreateClientOpen}
        item={item}
        onCreated={async (data) => {
          setSelectedEmail(data.client_email);
          setCreateClientOpen(false);
          await onChanged();
        }}
      />
    </div>
  );
}


function CreateClientDialog({ open, onOpenChange, item, onCreated }) {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    const ex = item.extracted || {};
    setFirst(ex.client_first_name || "");
    setLast(ex.client_last_name || "");
    setEmail(ex.client_email || "");
    setPhone(ex.client_phone || "");
    setDob(ex.client_dob || "");
  }, [open, item]);

  async function submit() {
    if (!first.trim() && !last.trim()) {
      toast.error("First or last name is required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(
        `/document-imports/items/${item.id}/create-client`,
        {
          client_first_name: first.trim() || null,
          client_last_name: last.trim() || null,
          client_email: email.trim() || null,
          client_phone: phone.trim() || null,
          client_dob: dob.trim() || null,
        },
      );
      const body = r.data;
      if (body.was_existing) {
        toast.success("Selected existing client", {
          description: body.client_email,
        });
      } else if (body.generated_email) {
        toast.success("Client stub created", {
          description: `Placeholder email: ${body.client_email}`,
        });
      } else {
        toast.success("Client stub created", { description: body.client_email });
      }
      onCreated?.(body);
    } catch (e) {
      toast.error("Couldn't create client", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-indigo-700" /> Create client from document
          </DialogTitle>
          <DialogDescription>
            Fields are pre-filled from what the AI extracted — please
            verify before saving. Leave email blank and we&apos;ll generate
            a placeholder <span className="font-mono text-xs">@no-email.local</span> address
            you can update later.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-2xs uppercase text-zinc-500">First name</label>
            <Input value={first} onChange={(e) => setFirst(e.target.value)}
                   data-testid="create-client-first" />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Last name</label>
            <Input value={last} onChange={(e) => setLast(e.target.value)}
                   data-testid="create-client-last" />
          </div>
          <div className="col-span-2">
            <label className="text-2xs uppercase text-zinc-500">Email (optional)</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="clinic@example.com or leave blank"
                   data-testid="create-client-email" />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Phone</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label className="text-2xs uppercase text-zinc-500">Date of birth</label>
            <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="create-client-confirm">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <UserPlus className="h-3.5 w-3.5 mr-1" />}
            Create &amp; select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
